import { useEffect, useRef } from "react";
import { Link, Navigate, Route, Routes, useLocation } from "react-router-dom";
import { ToastContainer } from "react-toastify";
import { IoHome } from "react-icons/io5";
import { IoHomeOutline } from "react-icons/io5";
import { FaRegUser } from "react-icons/fa";
import { FaUser } from "react-icons/fa";
import { IoWalletOutline } from "react-icons/io5";
import { IoWallet } from "react-icons/io5";
import { RiArrowUpDownLine } from "react-icons/ri";
import { RiArrowUpDownFill } from "react-icons/ri";
import { AiFillGolden } from "react-icons/ai";
import { LuHandshake } from "react-icons/lu";
import { FaHandshakeAngle } from "react-icons/fa6";
import { AiOutlineGold } from "react-icons/ai";
import { lazy, Suspense } from "react";

import { useAuth } from "./hooks/useAuth";
import api from "./api";
import { useQuery, useQueryClient } from "@tanstack/react-query";

import ProtectedRoute from "./components/ProtectedRoute";
import PublicRoute from "./components/PublicRoute";
import AdminRoute from "./components/AdminRoute";
import PushNotificationPrompt from "./components/PushNotificationPrompt";
const TradesReport = lazy(() => import("./Pages/admin/TradesReport"));
const ReferralStats = lazy(() => import("./Pages/admin/ReferralStats"));
const AdminsActivity = lazy(() => import("./Pages/admin/AdminsActivity"));
const ManagePermissions = lazy(() => import("./Pages/admin/ManagePermissions"));
const SendPushNotification = lazy(() => import("./Pages/admin/SendPushNotification"));
const WooCommerceTransactions = lazy(() => import("./Pages/admin/WooCommerceTransactions"));

const TradeSilver = lazy(() => import("./Pages/TradeSilver"));
const InviteFriends = lazy(() => import("./Pages/InviteFriends"));
const RateLimit = lazy(() => import("./Pages/RateLimit"));
const FAQ = lazy(() => import("./Pages/FAQ"));
const Rules = lazy(() => import("./Pages/Rules"));
const Home = lazy(() => import("./Pages/Home"));
const Trade = lazy(() => import("./Pages/Trade"));
const TradeGold = lazy(() => import("./Pages/TradeGold"));
const Orders = lazy(() => import("./Pages/Orders"));
const Wallet = lazy(() => import("./Pages/Wallet"));
const MyAccount = lazy(() => import("./Pages/MyAccount"));
const AccountLevelDetails = lazy(() => import("./Pages/AccountLevelDetails"));
const AboutUs = lazy(() => import("./Pages/About"));
const AccountDetails = lazy(() => import("./Pages/AccountDetails"));
const ManageCards = lazy(() => import("./Pages/ManageCards"));
const Deposit = lazy(() => import("./Pages/Deposit"));
const Withdraw = lazy(() => import("./Pages/Withdraw"));
const ChangePassword = lazy(() => import("./Pages/ChangePassword"));
const WithDrawsList = lazy(() => import("./Pages/admin/WithDrawsList"));
const History = lazy(() => import("./Pages/History"));
const PhysicalDeliveryHistory = lazy(
  () => import("./Pages/PhysicalDeliveryHistory"),
);
const AdminHome = lazy(() => import("./Pages/admin/AdminHome"));
const MonthlyReport = lazy(() => import("./Pages/admin/MonthlyReport"));
const UsersList = lazy(() => import("./Pages/admin/UsersList"));
const EditUser = lazy(() => import("./Pages/admin/EditUser"));
const CreateAccount = lazy(() => import("./Pages/admin/CreateAccount"));
const ChangeYourAccount = lazy(() => import("./Pages/admin/ChangeYourAccount"));
const PlatformSetting = lazy(() => import("./Pages/admin/PlatformSetting"));
const ManualPrices = lazy(() => import("./Pages/admin/ManualPrice"));
const Login = lazy(() => import("./Pages/Login"));
const Maintenance = lazy(() => import("./Pages/Maintenance"));
const PhysicalDeliveries = lazy(
  () => import("./Pages/admin/PhysicalDeliveries"),
);
const ResetPassword = lazy(() => import("./Pages/ResetPassword"));
const PaymentStatus = lazy(() => import("./Pages/PaymentStatus"));
const PhysicalDelivery = lazy(() => import("./Pages/PhysicalDelivery"));
const TradesList = lazy(() => import("./Pages/admin/TradesList"));
const DepositsList = lazy(() => import("./Pages/admin/DepositsList"));

function App() {
  const queryClient = useQueryClient();

  useEffect(() => {
    // چک کردن rate limit قبل از fetch کردن قیمت
    const isRateLimited = localStorage.getItem("rateLimitActive") === "true";
    const startTime = localStorage.getItem("rateLimitStartTime");
    
    if (isRateLimited && startTime) {
      const elapsed = Math.floor((Date.now() - parseInt(startTime)) / 1000);
      const RATE_LIMIT_DURATION = 15 * 60; // 15 دقیقه
      
      // اگه زمان گذشته، flag ها رو پاک کن و fetch کن
      if (elapsed >= RATE_LIMIT_DURATION) {
        localStorage.removeItem("rateLimitActive");
        localStorage.removeItem("rateLimitStartTime");
      } else {
        // هنوز rate limited هستیم، fetch نکن
        return;
      }
    }
    
    api.get("/price").then((res) => {
      queryClient.setQueryData(["price"], res.data);
    }).catch((error) => {
      // خطا رو ساکت ignore می‌کنیم چون usePrice خودش handle می‌کنه
      console.log("Initial price fetch failed:", error.message);
    });
  }, [queryClient]);

  // PWA codes
  const deferredPrompt = useRef(null);
  const shownInSession = useRef(false);

  useEffect(() => {
    const isStandalone =
      window.matchMedia("(display-mode: standalone)").matches ||
      window.navigator.standalone === true;

    if (isStandalone) return;

    const installOnce = async () => {
      if (!deferredPrompt.current || shownInSession.current) return;

      shownInSession.current = true;

      deferredPrompt.current.prompt();
      await deferredPrompt.current.userChoice;

      deferredPrompt.current = null;

      window.removeEventListener("click", installOnce);
    };

    const handler = (e) => {
      e.preventDefault();
      deferredPrompt.current = e;

      window.addEventListener("click", installOnce, { once: true });
    };

    window.addEventListener("beforeinstallprompt", handler);

    return () => {
      window.removeEventListener("beforeinstallprompt", handler);
      window.removeEventListener("click", installOnce);
    };
  }, []);
  // end PWA code

  // Offline Events
  useEffect(() => {
    const goOffline = () => {
      window.location.href = "/offline.html";
    };

    const goOnline = () => {
      if (window.location.pathname === "/offline.html") {
        window.location.href = "/";
      }
    };

    window.addEventListener("offline", goOffline);
    window.addEventListener("online", goOnline);

    return () => {
      window.removeEventListener("offline", goOffline);
      window.removeEventListener("online", goOnline);
    };
  }, []);
  // end Oflline Events

  // Helper function برای چک کردن rate limit status
  const checkRateLimitStatus = () => {
    const isRateLimited = localStorage.getItem("rateLimitActive") === "true";
    const startTime = localStorage.getItem("rateLimitStartTime");
    
    if (isRateLimited && startTime) {
      const elapsed = Math.floor((Date.now() - parseInt(startTime)) / 1000);
      const RATE_LIMIT_DURATION = 15 * 60; // 15 دقیقه
      
      // اگه زمان گذشته، flag ها رو پاک کن
      if (elapsed >= RATE_LIMIT_DURATION) {
        localStorage.removeItem("rateLimitActive");
        localStorage.removeItem("rateLimitStartTime");
        return false;
      }
      return true;
    }
    
    return false;
  };

  const isRateLimited = checkRateLimitStatus();

  const { data: maintenanceData } = useQuery({
    queryKey: ["checkMaintenance"],
    queryFn: async () => {
      const res = await api.get("/check-maintenance");
      return res.data.maintenanceMode;
    },
    retry: false,
    staleTime: 20 * 60 * 1000,
    gcTime: 20 * 60 * 1000,
    enabled: !isRateLimited, // غیرفعال کردن وقتی rate limited هستیم
    refetchOnWindowFocus: !isRateLimited,
    refetchOnReconnect: !isRateLimited,
  });

  const location = useLocation().pathname;
  const { data: authData } = useAuth();
  const isAuthenticated = authData?.isAuthenticated || false;

  // اگه rate limited هستیم، فقط صفحه RateLimit رو نشون بده
  if (isRateLimited && !location.includes("/rate-limit")) {
    return (
      <div className="sm:w-[500px] block mx-auto min-h-[90vh]">
        <Suspense
          fallback={
            <div className="flex items-center justify-center h-screen">
              <div className="text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
                <p className="mt-4 text-gray-600">در حال بارگذاری ...</p>
              </div>
            </div>
          }
        >
          <RateLimit />
        </Suspense>
      </div>
    );
  }

  return (
    <div className="sm:w-[500px] block mx-auto min-h-[90vh]">
      {maintenanceData && !location.startsWith("/admin-panel") ? (
        ["admin", "programmer"].some((role) =>
          authData?.user?.role.includes(role),
        ) ? (
          <Navigate to="/admin-panel" />
        ) : (
          <Maintenance />
        )
      ) : (
        <>
          <Suspense
            fallback={
              <div className="flex items-center justify-center h-screen">
                <div className="text-center">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
                  <p className="mt-4 text-gray-600">در حال بارگذاری ...</p>
                </div>
              </div>
            }
          >
            <Routes>
              <Route path="/rules" element={<Rules />} />
              <Route element={<PublicRoute />}>
                <Route path="/login" element={<Login />} />
                <Route path="/reset-password" element={<ResetPassword />} />
              </Route>

              <Route element={<ProtectedRoute />}>
                <Route path="*" element={<Home />} />
                <Route path="/" element={<Home />} />
                <Route path="/trade" element={<Trade />} />
                <Route path="/trade/gold" element={<TradeGold />} />
                <Route path="/trade/silver" element={<TradeSilver />} />
                <Route path="/orders" element={<Orders />} />
                <Route path="/wallet" element={<Wallet />} />
                <Route
                  path="/wallet/physical-delivery"
                  element={<PhysicalDelivery />}
                />
                <Route
                  path="/wallet/physical-delivery/history"
                  element={<PhysicalDeliveryHistory />}
                />
                <Route path="/wallet/deposit" element={<Deposit />} />
                <Route path="/wallet/withdraw" element={<Withdraw />} />
                <Route path="/my-account" element={<MyAccount />} />
                <Route
                  path="/my-account/details"
                  element={<AccountDetails />}
                />
                <Route
                  path="/my-account/account-level-details"
                  element={<AccountLevelDetails />}
                />
                <Route
                  path="/my-account/invite-friends"
                  element={<InviteFriends />}
                />
                <Route path="/my-account/about-us" element={<AboutUs />} />
                <Route path="/my-account/faq" element={<FAQ />} />
                <Route
                  path="/my-account/manage-cards"
                  element={<ManageCards />}
                />
                <Route path="/my-account/history" element={<History />} />
                <Route
                  path="/my-account/change-password"
                  element={<ChangePassword />}
                />
                <Route path="/payment-status" element={<PaymentStatus />} />
                <Route path="/rate-limit" element={<RateLimit />} />

                <Route element={<AdminRoute />}>
                  <Route path="/admin-panel" element={<AdminHome />} />
                  <Route
                    path="/admin-panel/physical-deliveries"
                    element={<PhysicalDeliveries />}
                  />
                  <Route
                    path="/admin-panel/deposits"
                    element={<DepositsList />}
                  />
                  <Route
                    path="/admin-panel/trades-report"
                    element={<TradesReport />}
                  />
                  <Route
                    path="/admin-panel/referral-stats"
                    element={<ReferralStats />}
                  />
                  <Route
                    path="/admin-panel/admins-activity"
                    element={<AdminsActivity />}
                  />
                  <Route
                    path="/admin-panel/manage-permissions"
                    element={<ManagePermissions />}
                  />
                  <Route
                    path="/admin-panel/send-push"
                    element={<SendPushNotification />}
                  />
                  <Route
                    path="/admin-panel/woocommerce-transactions"
                    element={<WooCommerceTransactions />}
                  />
                  <Route path="/admin-panel/trades" element={<TradesList />} />
                  <Route
                    path="/admin-panel/withdraws"
                    element={<WithDrawsList />}
                  />
                  <Route path="/admin-panel/users" element={<UsersList />} />
                  <Route
                    path="/admin-panel/edit-user/:id"
                    element={<EditUser />}
                  />
                  <Route
                    path="/admin-panel/create-account"
                    element={<CreateAccount />}
                  />
                  <Route
                    path="/admin-panel/change-your-account"
                    element={<ChangeYourAccount />}
                  />
                  <Route
                    path="/admin-panel/platform-setting"
                    element={<PlatformSetting />}
                  />
                  <Route
                    path="/admin-panel/manual-price"
                    element={<ManualPrices />}
                  />
                  <Route
                    path="/admin-panel/monthly-report"
                    element={<MonthlyReport />}
                  />
                </Route>
              </Route>
            </Routes>
          </Suspense>
          
          {/* درخواست دسترسی Push Notification */}
          {isAuthenticated && <PushNotificationPrompt />}
          
          <ToastContainer closeOnClick />

          {isAuthenticated &&
            !location.startsWith("/admin-panel") &&
            !location.startsWith("/my-account/change-password") && (
              <div className="fixed bottom-0 w-full sm:max-w-[500px] bg-white/95 backdrop-blur-md border-t border-gray-100 shadow-[0_-4px_20px_rgba(0,0,0,0.06)] grid grid-cols-4 place-items-center px-2 py-1.5 pb-[calc(0.375rem+env(safe-area-inset-bottom))]">
                {[
                  { to: "/",          label: "خانه",           active: location === "/",
                    icon:  <IoHomeOutline className="text-xl" />,
                    iconA: <IoHome      className="text-xl" /> },
                  { to: "/trade",     label: "معامله",         active: location === "/trade" || location.startsWith("/trade/"),
                    icon:  <RiArrowUpDownLine className="text-xl" />,
                    iconA: <RiArrowUpDownFill className="text-xl" /> },
                  { to: "/wallet",    label: "کیف پول",        active: location.startsWith("/wallet"),
                    icon:  <IoWalletOutline className="text-xl" />,
                    iconA: <IoWallet     className="text-xl" /> },
                  { to: "/my-account",label: "حساب کاربری",   active: location.startsWith("/my-account"),
                    icon:  <FaRegUser className="text-xl" />,
                    iconA: <FaUser    className="text-xl fill-secondary" /> },
                ].map(({ to, label, active, icon, iconA }) => (
                  <Link
                    key={to}
                    to={to}
                    className="relative flex flex-col items-center gap-0.5 w-full py-1.5 rounded-xl transition-colors"
                  >
                    <span className={`flex items-center justify-center w-9 h-9 rounded-xl transition-all ${
                      active ? "bg-secondary/15 text-secondary scale-105" : "text-gray-400"
                    }`}>
                      {active ? iconA : icon}
                    </span>
                    <span className={`text-[10px] font-medium transition-colors ${active ? "text-secondary" : "text-gray-400"}`}>
                      {label}
                    </span>
                    {active && (
                      <span className="absolute bottom-0 left-1/2 -translate-x-1/2 w-1 h-1 rounded-full bg-secondary" />
                    )}
                  </Link>
                ))}
              </div>
            )}

          {isAuthenticated && location.startsWith("/admin-panel") && (
            <div className="fixed bottom-0 w-full sm:max-w-[500px] bg-white/95 backdrop-blur-md border-t border-gray-100 shadow-[0_-4px_20px_rgba(0,0,0,0.06)] grid grid-cols-5 place-items-center px-1 py-1.5 pb-[calc(0.375rem+env(safe-area-inset-bottom))]">
              {[
                { to: "/admin-panel",                   label: "خانه",         active: location === "/admin-panel",
                  icon:  <IoHomeOutline className="text-xl" />,
                  iconA: <IoHome      className="text-xl" /> },
                { to: "/admin-panel/trades",            label: "معاملات",      active: location === "/admin-panel/trades",
                  icon:  <LuHandshake     className="text-xl" />,
                  iconA: <FaHandshakeAngle className="text-xl" /> },
                { to: "/admin-panel/physical-deliveries", label: "تحویل",     active: location === "/admin-panel/physical-deliveries",
                  icon:  <AiOutlineGold className="text-xl" />,
                  iconA: <AiFillGolden  className="text-xl" /> },
                { to: "/admin-panel/withdraws",         label: "برداشت‌ها",    active: location === "/admin-panel/withdraws",
                  icon:  <IoWalletOutline className="text-xl" />,
                  iconA: <IoWallet     className="text-xl" /> },
                { to: "/admin-panel/users",             label: "کاربران",      active: location.startsWith("/admin-panel/users"),
                  icon:  <FaRegUser className="text-xl" />,
                  iconA: <FaUser    className="text-xl fill-secondary" /> },
              ].map(({ to, label, active, icon, iconA }) => (
                <Link
                  key={to}
                  to={to}
                  className="relative flex flex-col items-center gap-0.5 w-full py-1.5 rounded-xl transition-colors"
                >
                  <span className={`flex items-center justify-center w-9 h-9 rounded-xl transition-all ${
                    active ? "bg-secondary/15 text-secondary scale-105" : "text-gray-400"
                  }`}>
                    {active ? iconA : icon}
                  </span>
                  <span className={`text-[10px] font-medium transition-colors ${active ? "text-secondary" : "text-gray-400"}`}>
                    {label}
                  </span>
                  {active && (
                    <span className="absolute bottom-0 left-1/2 -translate-x-1/2 w-1 h-1 rounded-full bg-secondary" />
                  )}
                </Link>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default App;
